import { useEffect, useState } from "react";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { useGetSafetyStatsQuery } from "../dashboard/api/safetyApiSlice";
import { useTranslation } from "react-i18next";
import Loading from "components/commons/Loading";
import ErrorMessage from "components/commons/ErrorMessage";

const COLORS = ["rgb(150, 150, 150)", "rgb(221, 221, 221)"];

const SafetyStatsCard = () => {

    const [t] = useTranslation(["global", "safety"])

    const [selectedDays, setSelectedDays] = useState(30);
    const { data: stats, isFetching, isSuccess, isError, error, refetch } = useGetSafetyStatsQuery(selectedDays);


    // Refetch automatico su cambio filtro
    useEffect(() => {
        refetch({ companyGuid: "abc", durationDays: selectedDays });
    }, [selectedDays]);

    function getStatusColor(status) {
        switch (status) {
            case "OPEN": return "rgb(190, 48, 48)"
            case "IN_PROGRESS": return "rgb(26, 75, 221)"
            case "CANCELED": return "gray"
            case "CLOSED": return "rgb(25, 135, 84)"
            default: return "bg-dark"
        }
    }

    const toPieData = (map, key) => {
        const total = Object.values(map).reduce((sum, val) => sum + val, 0);
        return Object.entries(map).map(([rawName, value]) => {
            const localizedName =
                key === "status"
                    ? t(`safety:entity.alarm.status.${rawName}`)
                    : t(`safety:entity.alarm.type.${rawName}`)
            return {
                name: localizedName,
                rawName, // manteniamo l’originale per colori
                value,
                percentage: ((value / total) * 100).toFixed(0) + "%",
            }
        });
    };

    const renderPieChart = (data, title, type) => (
        <div className="col-md-6 mb-4">

            <h5 className="card-title text-center">{title}</h5>
            {data.length === 0 ? (
                <p className="text-center text-muted">{t("helper.no_data")}</p>
            ) : (
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            stroke="#000000"
                            strokeOpacity={0.2}
                            strokeWidth={5}
                            outerRadius={100}
                            innerRadius={50}
                            label={({ name, percentage, value, percent }) => `${name} ${value}`}
                        >

                            {data.map((entry, index) => (
                                type == "type" ?
                                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                    : <Cell key={index} fill={getStatusColor(entry.name)} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value) => `${value}`} />
                    </PieChart>
                </ResponsiveContainer>
            )}

        </div>
    );

    const renderKpiCard = (title, seconds) => {
        const minutes = Math.round(seconds / 60);
        return (
            <div className="col-md-4 mb-4">
                <div className="card bg-dark text-bg-light h-100">
                    <div className="card-body text-center">
                        <h6 className="card-subtitle mb-2 text-muted">{title}</h6>
                        <h3 className="card-title">{minutes} min</h3>
                    </div>
                </div>
            </div>
        );
    };

    const renderSecondaryKpiCard = (title, value, unit) => {
        return (
            <div className="col-md-4 mb-4">
                <div className="card bg-dark bg-opacity-25 text-bg-light h-100">
                    <div className="card-body text-center">
                        <h6 className="card-subtitle mb-2 text-muted">{title}</h6>
                        <h3 className="card-title">{value} {unit}</h3>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={`container mt-4 ${isFetching ? "opacity-25" : ""}`}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0 text-muted"><i className="fa-solid fa-shield"></i> {t("safety:menu.stats")}</h4>
                {isFetching && <Loading />}
                <select
                    className="form-select w-auto"
                    value={selectedDays}
                    onChange={(e) => setSelectedDays(Number(e.target.value))}
                >
                    <option value={7}>{t("time.last_n_days", { days: 7 })}</option>
                    <option value={30}>{t("time.last_n_days", { days: 30 })}</option>
                    <option value={90}>{t("time.last_n_days", { days: 90 })}</option>
                    <option value={0}>{t("particles.all")}</option>
                </select>
            </div>

            {isError && <ErrorMessage error={error} />}

            {isSuccess && (
                <>
                    <div className="row">
                        <div className="col-md-4 mb-4">
                            <div className="card bg-dark text-bg-light h-100">
                                <div className="card-body text-center">
                                    <h6 className="card-subtitle mb-2 text-muted">{t("safety:label.total")}</h6>
                                    <h3 className="card-title">{t("safety:entity.alarm.alarmWithCount", { count: stats.count })}</h3>
                                </div>
                            </div>
                        </div>
                        {renderKpiCard(t("safety:label.avg_taken_in_charge"), stats.avgTakenInChargeSeconds)}
                        {renderKpiCard(t("safety:label.avg_solved"), stats.avgResolutionSeconds)}
                    </div>
                    <div className="d-flex justify-content-center">
                        {renderPieChart(toPieData(stats.types), t("safety:label.alarms_by_type"), "type")}
                        {/* {renderPieChart(toPieData(stats.status), t("safety:label.alarms_by_status"), "status")} */}
                    </div>
                    <div className="row">
                        <div className="col-md-4 mb-4">
                            <div className="card bg-dark bg-opacity-25 text-bg-light h-100">
                                <div className="card-body text-center">
                                    <h6 className="card-subtitle mb-2 text-muted">{t("safety:label.localized")}</h6>
                                    <h3 className="card-title">{stats.count > 0 ? ((stats.localized / stats.count) * 100).toFixed(0) : 0} %</h3>
                                </div>
                            </div>
                        </div>
                        {renderSecondaryKpiCard(t("safety:label.avg_loc_time"), Math.round(stats.avgLocalizationSeconds / 60), "min")}
                        {renderSecondaryKpiCard(t("safety:label.avg_loc_accuracy"), stats.avgLocalizationAccuracyMeters, "m")}
                    </div>
                </>
            )}
        </div>
    );
};

export default SafetyStatsCard;
